// Facility management types

// Grid-based time slot (each hour of the day)
export interface GridTimeSlot {
  id: string;        // "slot-0", "slot-1", etc. or "00:00"
  time: string;      // "00:00", "01:00", etc.
  isAvailable: boolean;
  isBooked: boolean;
}

// Legacy range-based time slot (for backwards compatibility)
export interface TimeSlot {
  startTime: string; // "09:00"
  endTime: string;   // "10:00"
  price?: number;
  isAvailable?: boolean;
}

// Schedule using grid-based slots (matches mobile)
export interface FacilityAvailability {
  weekdays: GridTimeSlot[];
  saturday: GridTimeSlot[];
  sunday: GridTimeSlot[];
  specificDates: { [date: string]: GridTimeSlot[] };
}

// Legacy schedule structure (for backwards compatibility)
export interface FacilitySchedule {
  weekdays: TimeSlot[];
  saturday: TimeSlot[];
  sunday: TimeSlot[];
}

export interface DateSpecificSlots {
  [date: string]: TimeSlot[];
}

export interface Facility {
  id: string;
  name: string;
  type: string;
  description: string;
  city: string;
  street: string;
  postalCode: string;
  pricePerHour: number;
  grossPricePerHour?: number;
  vatRate?: number;
  minBookingSlots?: number;
  maxUsers?: number;
  pricePerUser?: boolean;
  availability?: FacilityAvailability;
  // Legacy fields for backwards compatibility
  allTimeSlots?: FacilitySchedule;
  dateSpecificSlots?: DateSpecificSlots;
  businessProfileId: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateFacilityRequest {
  name: string;
  type: string;
  description: string;
  city: string;
  street: string;
  postalCode: string;
  pricePerHour: number;
  grossPricePerHour?: number;
  vatRate?: number;
  minBookingSlots?: number;
  maxUsers?: number;
  pricePerUser?: boolean;
  businessProfileId?: string;
  availability?: FacilityAvailability;
  // Legacy fields
  allTimeSlots?: FacilitySchedule;
  dateSpecificSlots?: DateSpecificSlots;
}

export interface UpdateFacilityRequest extends CreateFacilityRequest {}

export interface FacilityCategory {
  id: string;
  name: string;
  description?: string;
}

// Helper function to generate default 24-hour time slots
export function generateTimeSlots(): GridTimeSlot[] {
  const slots: GridTimeSlot[] = [];
  for (let hour = 0; hour < 24; hour++) {
    const time = `${hour.toString().padStart(2, '0')}:00`;
    slots.push({
      id: `slot-${hour}`,
      time,
      isAvailable: false,
      isBooked: false,
    });
  }
  return slots;
}

// Helper to create empty availability schedule
export function createEmptyAvailability(): FacilityAvailability {
  return {
    weekdays: generateTimeSlots(),
    saturday: generateTimeSlots(),
    sunday: generateTimeSlots(),
    specificDates: {},
  };
}

interface ScheduleSlotLike {
  id?: string;
  time: string;
  isAvailable: boolean;
  isBooked?: boolean;
}

// Builds a new facility's default availability from the business's own
// default schedule (Business Profile > Harmonogram Dostępności), so a new
// facility doesn't start fully closed. Booked flags are never copied - a
// new facility has no bookings of its own yet.
export function createAvailabilityFromBusinessSchedule(schedule: {
  weekdaysSchedule?: ScheduleSlotLike[];
  saturdaySchedule?: ScheduleSlotLike[];
  sundaySchedule?: ScheduleSlotLike[];
}): FacilityAvailability {
  const toGridSlots = (slots?: ScheduleSlotLike[]): GridTimeSlot[] => {
    if (!slots || slots.length === 0) return generateTimeSlots();
    return slots.map((slot, index) => ({
      id: slot.id || `slot-${index}`,
      time: slot.time,
      isAvailable: slot.isAvailable,
      isBooked: false,
    }));
  };

  return {
    weekdays: toGridSlots(schedule.weekdaysSchedule),
    saturday: toGridSlots(schedule.saturdaySchedule),
    sunday: toGridSlots(schedule.sundaySchedule),
    specificDates: {},
  };
}
